/* eslint-disable @typescript-eslint/no-unused-vars */
import { posthog } from 'posthog-js';
import { getCookie } from './cookies-handlers';
import {
  getCurrentToken,
  getGleapAnonymousUserId,
  getGleapAnonymousUserHash,
  getGleapUserHash,
} from './token';

// User identification flag — resets on every page load
let isUserIdentified = false;
let eventQueue: Array<{ method: string; args: any[] }> = [];
let isGleapContextMenuGuardInstalled = false;

type PosthogBootstrapIds = {
  distinctID: string;
  sessionID: string;
};

const setUserIdentified = (status: boolean): void => {
  isUserIdentified = status;
};

const readPosthogBootstrapIdsFromUrl = (): PosthogBootstrapIds | undefined => {
  if (typeof window === 'undefined') return undefined;

  const readFromSearch = new URLSearchParams(window.location.search);
  const readFromHash = new URLSearchParams(window.location.hash.slice(1));

  const distinctId =
    readFromSearch.get('distinct_id') || readFromHash.get('distinct_id');
  const sessionId =
    readFromSearch.get('session_id') || readFromHash.get('session_id');

  if (!distinctId || !sessionId) return undefined;

  return {
    distinctID: distinctId,
    sessionID: sessionId,
  };
};

/**
 * PostHog Web Analytics breakdowns (Browser / OS / Device / Viewport) use event
 */
function getPosthogStyleClientProperties(): Record<string, string | number> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  const ua = navigator.userAgent;
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const deviceType = isMobile ? 'Mobile' : 'Desktop';

  let os = 'Unknown';
  if (/Windows NT/i.test(ua)) {
    os = 'Windows';
  } else if (/Mac OS X|Macintosh/i.test(ua)) {
    os = 'Mac OS X';
  } else if (/Android/i.test(ua)) {
    os = 'Android';
  } else if (/iPhone|iPad|iPod/i.test(ua)) {
    os = 'iOS';
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
  }

  const parseMajor = (m: RegExpMatchArray | null): number => {
    const n = m?.[1] ? parseInt(m[1], 10) : NaN;
    return Number.isFinite(n) ? n : 0;
  };

  let browser = 'Unknown';
  let browserVersion = 0;

  if (/Edg\//i.test(ua)) {
    browser = 'Microsoft Edge';
    browserVersion = parseMajor(ua.match(/Edg\/(\d+)/i));
  } else if (/Firefox\//i.test(ua)) {
    browser = 'Firefox';
    browserVersion = parseMajor(ua.match(/Firefox\/(\d+)/i));
  } else if (/CriOS\//i.test(ua)) {
    browser = 'Chrome iOS';
    browserVersion = parseMajor(ua.match(/CriOS\/(\d+)/i));
  } else if (/Chrome\//i.test(ua)) {
    browser = 'Chrome';
    browserVersion = parseMajor(ua.match(/Chrome\/(\d+)/i));
  } else if (/Safari\//i.test(ua) && /Version\//i.test(ua)) {
    browser = /Mobile\/\S+/i.test(ua) ? 'Mobile Safari' : 'Safari';
    browserVersion = parseMajor(ua.match(/Version\/(\d+)/i));
  } else if (/OPR\//i.test(ua)) {
    browser = 'Opera';
    browserVersion = parseMajor(ua.match(/OPR\/(\d+)/i));
  }

  const viewportWidth =
    typeof window.innerWidth === 'number' ? Math.round(window.innerWidth) : 0;
  const viewportHeight =
    typeof window.innerHeight === 'number' ? Math.round(window.innerHeight) : 0;

  return {
    $browser: browser,
    $browser_version: browserVersion,
    $os: os,
    $device_type: deviceType,
    /** Used by Web Analytics Viewport breakdowns (same idea as Browser / OS). */
    $viewport_width: viewportWidth,
    $viewport_height: viewportHeight,
  };
}

/**
 * Returns true when value is a plain object (not array, string, null, etc.).
 * Prevents spreading non-object parameters that would create numeric property keys
 * (e.g. spreading a string 'abc' produces {0:'a',1:'b',2:'c'}).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * API / Redux may send null for missing last name. Default `''` in destructuring
 * does not apply when the value is explicitly null, and template strings would
 * otherwise produce "John null".
 */
function normalizePersonNamePart(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return String(value).trim();
}

/** First + last name for analytics; omits missing parts (e.g. no last name from API). */
export function buildPersonDisplayName(
  firstName?: string | null,
  lastName?: string | null,
): string | undefined {
  const parts = [
    normalizePersonNamePart(firstName),
    normalizePersonNamePart(lastName),
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

/**
 * Track event to PostHog via direct fetch.
 */
const trackPosthogEvent = (
  eventName: string,
  parameters?: Record<string, any>,
): void => {
  const posthogToken =
    (posthog as any).config?.token || process.env.REACT_APP_POSTHOG_TOKEN;
  const posthogApiHost =
    (posthog as any).config?.api_host ||
    process.env.REACT_APP_POSTHOG_API_HOST ||
    'https://us.i.posthog.com';
  const posthogDistinctId = posthog.get_distinct_id?.();
  const posthogSessionId = posthog.get_session_id?.();

  if (posthogToken && posthogDistinctId) {
    const safeParameters = isPlainObject(parameters) ? parameters : {};
    const eventProperties = {
      ...getPosthogStyleClientProperties(),
      ...safeParameters,
      $current_url: window.location.href,
      ...(posthogSessionId ? { $session_id: String(posthogSessionId) } : {}),
    };

    fetch(`${posthogApiHost}/capture/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: posthogToken,
        event: eventName,
        distinct_id: posthogDistinctId,
        properties: eventProperties,
        timestamp: new Date().toISOString(),
      }),
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[PostHog] Failed to send event:', err);
    });
  }
};

export const capturePosthogEventManually = (
  eventName: string,
  parameters?: Record<string, any>,
): void => {
  trackPosthogEvent(eventName, parameters);
};

/**
 * Browser URL at the moment of capture. Use on critical events (e.g. Signup completed) so
 * PostHog "URL / Screen" matches the signup step; otherwise posthog-js may attach
 * $current_url when the SPA has already navigated (often showing only https://app.trulyinbox.com/).
 */
export function getPosthogPageSnapshotAtCapture(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  return {
    $current_url: window.location.href,
    signup_step_path: `${window.location.pathname}${window.location.search}`,
  };
}

/**
 * Capture a PostHog event via the SDK (posthog.capture).
 */
export const capturePosthogEvent = (
  eventName: string,
  parameters?: Record<string, any>,
): void => {
  try {
    posthog.capture(eventName, parameters);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to capture PostHog event:', error);
  }
};

/**
 * Normalize queued track args to { eventName, parameters }
 * Handles both call signatures:
 *   - Standard:    (eventName, properties)
 *   - Object form: ({ event, properties, userId })
 *
 * Only forwards parameters when they are a plain object. Any other type
 * (string, array, function, etc.) is discarded to prevent numeric property keys
 * being created in PostHog when a non-object is accidentally spread.
 */
const normalizeTrackArgs = (
  args: any[],
): { eventName: string; parameters?: Record<string, any> } => {
  if (args.length >= 1 && isPlainObject(args[0]) && args[0]?.event) {
    const props = args[0].properties;
    return {
      eventName: String(args[0].event),
      parameters: isPlainObject(props) ? props : undefined,
    };
  }
  const props = args[1];
  return {
    eventName: typeof args[0] === 'string' ? args[0] : String(args[0] ?? ''),
    parameters: isPlainObject(props) ? props : undefined,
  };
};

const flushEventQueue = (): void => {
  eventQueue.forEach(({ method, args }) => {
    if (method === 'track') {
      const { eventName, parameters } = normalizeTrackArgs(args);
      trackPosthogEvent(eventName, parameters);
    }
  });
  eventQueue = [];
};

/**
 * Track a product analytics event to PostHog.
 *
 * Events fired before the user is identified are queued and flushed once
 * `initializeThirdPartyIntegrations` runs, so early events keep their
 * attribution.
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>,
): void => {
  if (isUserIdentified) {
    trackPosthogEvent(eventName, properties);
  } else {
    eventQueue.push({ method: 'track', args: [eventName, properties] });
  }
};

export const initializePosthog = (posthogToken: string): void => {
  if (!posthogToken) return;

  // eslint-disable-next-line no-underscore-dangle
  if ((posthog as any).__loaded) {
    return;
  }

  const bootstrapIds = readPosthogBootstrapIdsFromUrl();

  const posthogApiHost =
    process.env.REACT_APP_POSTHOG_API_HOST || 'https://us.i.posthog.com';

  posthog.init(posthogToken, {
    api_host: posthogApiHost, // your managed reverse proxy domain
    ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
    persistence: 'localStorage+cookie',
    autocapture: true,
    capture_exceptions: true,
    bootstrap: bootstrapIds,
  });

  // Capture marketing referrer and UTM params forwarded from the marketing site
  // (trulyinbox.com enriches signup links with ?ref=/page&utm_source=…)
  const params = new URLSearchParams(window.location.search);
  const marketingRef = params.get('ref');
  if (marketingRef) {
    posthog.register({
      $marketing_ref: marketingRef,
      marketing_utm_source: params.get('utm_source'),
      marketing_utm_medium: params.get('utm_medium'),
      marketing_utm_campaign: params.get('utm_campaign'),
      marketing_utm_term: params.get('utm_term'),
      marketing_utm_content: params.get('utm_content'),
    });
    posthog.people.set_once({
      $initial_marketing_ref: marketingRef,
    });
  }
};

/**
 * Ensures posthog-js is initialized when REACT_APP_POSTHOG_TOKEN is set.
 * Call before critical client events when identify/init ordering may run late (e.g. class lifecycle).
 */
export function ensurePosthogInitialized(): boolean {
  const posthogToken = process.env.REACT_APP_POSTHOG_TOKEN;
  if (!posthogToken) {
    return false;
  }
  // eslint-disable-next-line no-underscore-dangle
  if (!(posthog as any).__loaded) {
    initializePosthog(posthogToken);
  }
  return true;
}

/**
 * Capture broken_screen_shown via direct fetch (more reliable than SDK-only capture
 * for critical error-boundary events that must reach PostHog immediately).
 */
export function captureBrokenScreenShown(
  parameters?: Record<string, any>,
): void {
  ensurePosthogInitialized();
  capturePosthogEventManually('broken_screen_shown', parameters);
}

export const attachPosthogIdsToUrl = (url: string): string => {
  if (!url || typeof window === 'undefined') return url;

  const distinctId = posthog.get_distinct_id?.();
  const sessionId = posthog.get_session_id?.();

  if (!distinctId || !sessionId) return url;

  try {
    const nextUrl = new URL(url, window.location.origin);
    const hashParams = new URLSearchParams(nextUrl.hash.replace(/^#/, ''));

    hashParams.set('distinct_id', String(distinctId));
    hashParams.set('session_id', String(sessionId));

    nextUrl.hash = hashParams.toString();
    return nextUrl.toString();
  } catch (error) {
    return url;
  }
};

export const resetUserIdentification = (): void => {
  setUserIdentified(false);
  eventQueue = [];

  posthog.reset();

  // eslint-disable-next-line no-underscore-dangle
  window._cio?.reset?.();
};

type InitializeThirdPartyIntegrations = {
  trackingId: string;
  showChatSupport?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
};

export const initializeThirdPartyIntegrations = ({
  trackingId,
  showChatSupport = false,
  firstName,
  lastName,
  email = '',
}: InitializeThirdPartyIntegrations): void => {
  const safeFirst = normalizePersonNamePart(firstName);
  const safeLast = normalizePersonNamePart(lastName);
  const displayName = buildPersonDisplayName(firstName, lastName);

  if (trackingId) {
    setUserIdentified(true);

    // Freshworks CRM Initialization
    window.fwcrm &&
      window.fwcrm.identify(trackingId, {
        'First Name': safeFirst,
        'Last Name': safeLast,
        Email: email,
      });

    // Profitwell Initialization
    window.profitwell && window.profitwell('start', { 'User Email': email });

    // Customer.io User Identification (enables in-app message targeting)
    /* eslint-disable no-underscore-dangle */
    window._cio &&
      window._cio.identify({
        id: trackingId,
        email,
        ...(safeFirst ? { first_name: safeFirst } : {}),
        ...(safeLast ? { last_name: safeLast } : {}),
      });
    /* eslint-enable no-underscore-dangle */

    // Ensure PostHog is initialized before identifying (fixes ordering bug:
    // componentDidUpdate runs before useEffect, so initializePosthog from
    // usePosthogIntegrationWithConfig may not have run yet)
    ensurePosthogInitialized();

    // PostHog User Identification ($email / $name improve Person display in Activity)
    const referrer = getCookie('shreferer');
    posthog.identify(trackingId, {
      email,
      ...(displayName ? { name: displayName, $name: displayName } : {}),
      $email: email,
      ...(safeFirst ? { 'First Name': safeFirst } : {}),
      ...(safeLast ? { 'Last Name': safeLast } : {}),
      ...(referrer ? { $referrer: referrer } : {}),
    });

    // Flush any events queued before user was identified
    flushEventQueue();
  }
};

const CHURNKEY_SCRIPT_ID = 'churnkey-script';

export const initializeChurnKey = (appId: string): void => {
  if (
    !appId ||
    document.getElementById(CHURNKEY_SCRIPT_ID) ||
    window.churnkey?.created
  ) {
    return;
  }

  window.churnkey = { created: true };

  const script = document.createElement('script');
  script.id = CHURNKEY_SCRIPT_ID;
  script.src = `https://assets.churnkey.co/js/app.js?appId=${appId}`;
  script.async = true;
  script.defer = true;

  const appendTarget = document.head ?? document.body;
  const firstScript = document.getElementsByTagName('script')[0];
  const parent = firstScript?.parentNode ?? appendTarget;

  try {
    if (firstScript && parent.contains(firstScript)) {
      parent.insertBefore(script, firstScript);
    } else {
      appendTarget.appendChild(script);
    }
  } catch (error) {
    appendTarget.appendChild(script);
    // eslint-disable-next-line no-console
    console.error('Failed to inject ChurnKey script:', error);
  }
};

const CUSTOMERIO_SCRIPT_ID = 'cio-tracker';

export const initializeCustomerio = (siteId: string): void => {
  /* eslint-disable no-underscore-dangle */
  if (!siteId || document.getElementById(CUSTOMERIO_SCRIPT_ID) || window._cio) {
    return;
  }

  const cioMethods = [
    'load',
    'identify',
    'sidentify',
    'track',
    'page',
    'on',
    'off',
  ];
  const cioQueue: unknown[] = [];
  cioMethods.forEach((method) => {
    (cioQueue as any)[method] = (...args: unknown[]) => {
      cioQueue.push([method, ...args]);
    };
  });
  window._cio = cioQueue;
  /* eslint-enable no-underscore-dangle */

  const script = document.createElement('script');
  script.id = CUSTOMERIO_SCRIPT_ID;
  script.async = true;
  script.setAttribute('data-site-id', siteId);
  script.setAttribute('data-use-array-params', 'true');
  script.setAttribute('data-use-in-app', 'true');
  script.src = 'https://assets.customer.io/assets/track.js';

  script.onerror = () => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Customer.io track.js failed to load');
    }
  };

  const appendTarget = document.head ?? document.body;
  const firstScript = document.getElementsByTagName('script')[0];
  const parent = firstScript?.parentNode ?? appendTarget;

  try {
    if (firstScript && parent.contains(firstScript)) {
      parent.insertBefore(script, firstScript);
    } else {
      appendTarget.appendChild(script);
    }
  } catch (error) {
    appendTarget.appendChild(script);
    // eslint-disable-next-line no-console
    console.error('Failed to inject Customer.io script:', error);
  }
};

/**
 * Gleap's bug-report/screenshot flow renders its UI inside a `srcdoc` iframe that
 * clones the host page's DOM (including this app's own <script> tags) to produce
 * a live visual snapshot. Because `srcdoc` iframes are same-origin, right-clicking
 * inside them surfaces the native browser "Reload frame" context-menu item;
 * selecting it re-executes the cloned <script> tags, rebooting the whole app
 * inside the iframe. This suppresses that native context menu on Gleap's iframe(s).
 * Safe no-op for a genuinely cross-origin iframe — contentDocument access just fails.
 */
const isLikelyGleapIframe = (iframe: HTMLIFrameElement): boolean => {
  const haystack = [
    iframe.id,
    iframe.className,
    iframe.getAttribute('src') ?? '',
    iframe.getAttribute('title') ?? '',
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes('gleap');
};

const blockContextMenu = (event: Event): void => {
  event.preventDefault();
};

const attachContextMenuSuppressor = (iframe: HTMLIFrameElement): void => {
  const tryAttach = (): void => {
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.removeEventListener('contextmenu', blockContextMenu);
      doc.addEventListener('contextmenu', blockContextMenu);
    } catch {
      // Genuinely cross-origin iframe — contentDocument is inaccessible.
      // Intentional silent no-op; native context menu behavior is unchanged.
    }
  };

  tryAttach();
  iframe.addEventListener('load', tryAttach);
};

const scanNodeForGleapIframes = (node: Node): void => {
  if (!(node instanceof HTMLElement)) return;

  const candidates: HTMLIFrameElement[] =
    node instanceof HTMLIFrameElement
      ? [node]
      : Array.from(node.querySelectorAll('iframe'));

  candidates.filter(isLikelyGleapIframe).forEach(attachContextMenuSuppressor);
};

export const suppressGleapIframeContextMenu = (): void => {
  if (typeof document === 'undefined') return;
  if (isGleapContextMenuGuardInstalled) return;
  isGleapContextMenuGuardInstalled = true;

  Array.from(document.querySelectorAll('iframe'))
    .filter(isLikelyGleapIframe)
    .forEach(attachContextMenuSuppressor);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(scanNodeForGleapIframes);
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

export const initializeGleap = (apiKey: string) => {
  if (!apiKey) return;

  // Prevent duplicate script injection
  if (
    document.getElementById('gleap-script') ||
    (window as any).Gleap?.invoked
  ) {
    return;
  }

  suppressGleapIframeContextMenu();

  try {
    // Initialize Gleap global object with proxy (similar to the HTML script)
    if (!(window as any).Gleap || !(window as any).Gleap.invoked) {
      (window as any).GleapActions = [];
      const gleapProxy = new Proxy(
        { invoked: true },
        {
          get(target, prop) {
            if (prop === 'invoked') {
              return target.invoked;
            }
            return (...args: any[]) => {
              (window as any).GleapActions.push({ e: prop, a: args });
            };
          },
          set(target, prop, value) {
            (target as any)[prop] = value;
            return true;
          },
        },
      );
      (window as any).Gleap = gleapProxy;

      // Load Gleap script
      const gleapScript = document.createElement('script');
      gleapScript.id = 'gleap-script';
      gleapScript.type = 'text/javascript';
      gleapScript.async = true;
      gleapScript.src = 'https://sdk.gleap.io/latest/index.js';

      const head = document.getElementsByTagName('head')[0];
      if (head) {
        head.appendChild(gleapScript);
      }

      // Initialize Gleap with API key
      (window as any).Gleap.initialize(apiKey);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('Failed to inject Gleap script:', error);
    }
    throw error;
  }
};

/** Tiny non-cryptographic hex hash — NOT a real HMAC (that requires a
 *  backend secret). Only meant to avoid a static, hardcoded hash string. */
const hashToHex = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) % 2147483647;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
};

/** Gleap identity when no session (login/signup, etc.); regenerated each
 *  page load instead of a static value. See hashToHex caveat above. */
const GLEAP_ANONYMOUS_USER_ID = `anonomous_user_${Date.now()}`;
const GLEAP_ANONYMOUS_USER_HASH = hashToHex(GLEAP_ANONYMOUS_USER_ID);

type GleapUser = {
  shAccountId: string;
  userId?: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  planName?: string;
  paddleCustomerLink?: string;
  apusProfileLink?: string;
};

const gleapIdentify = (
  gleap: any,
  id: string,
  attributes: Record<string, string>,
  hash: string | undefined,
): void => {
  try {
    gleap.identify?.(id, attributes, hash);
  } catch (error) {
    if (process.env.REACT_APP_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.warn('Failed to identify Gleap user:', error);
    }
  }
};

const identifyAnonymousGleapUser = (gleap: any): void => {
  if (getCurrentToken()) return;

  // Prefer the dedicated anonymous hash (never overwritten by session hash).
  // Fall back to the shared gleapUserHash for backwards-compatibility with
  // devices that have not yet received the new key.
  const cachedGleapHash = getGleapAnonymousUserHash() || getGleapUserHash();
  const cachedAnonymousUserId = getGleapAnonymousUserId();

  if (cachedGleapHash && cachedAnonymousUserId) {
    gleapIdentify(
      gleap,
      cachedAnonymousUserId,
      { name: '--', email: '--', userId: cachedAnonymousUserId, plan: '' },
      cachedGleapHash,
    );
    return;
  }

  gleapIdentify(
    gleap,
    GLEAP_ANONYMOUS_USER_ID,
    { name: '--', email: '--', userId: GLEAP_ANONYMOUS_USER_ID, plan: '' },
    GLEAP_ANONYMOUS_USER_HASH,
  );
};

export const setGleapUser = ({
  shAccountId,
  userId,
  firstName,
  lastName,
  email,
  planName,
  paddleCustomerLink,
  apusProfileLink,
}: GleapUser): void => {
  const gleap: any = (window as any).Gleap;

  if (!gleap) return;

  // No user yet (e.g. login/signup): identify with static anonymous id + hash from FE
  if (!email) {
    identifyAnonymousGleapUser(gleap);
    return;
  }

  // Handles missing lastName (or firstName): only truthy parts are joined
  const name = [firstName, lastName]?.filter(Boolean)?.join(' ')?.trim();
  const gleapUserHash = getGleapUserHash();

  gleapIdentify(
    gleap,
    userId != null ? String(userId) : '--',
    {
      name: name || '--',
      email,
      userId: userId != null ? String(userId) : '--',
      plan: planName || '',
      // Empty for Stripe/internal accounts, so a stale link never sticks around
      paddleCustomerLink: paddleCustomerLink || '',
      // Empty when the email is missing, so a stale link never sticks around
      apusProfileLink: apusProfileLink || '',
      ...(userId != null ? { shAccountId: String(shAccountId) } : {}),
    },
    gleapUserHash || GLEAP_ANONYMOUS_USER_ID,
  );
};

export const clearGleapUser = (): void => {
  const gleap: any = (window as any).Gleap;

  if (!gleap) return;

  try {
    // Prefer explicit identity clearing if available
    gleap.clearIdentity?.();
    gleap.logout?.();
  } catch (error) {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.warn('Failed to clear Gleap user identity:', error);
    }
  }
};

export const initializePaddle = (token: string, customerId?: string) => {
  if (!token) return;

  // Check if Paddle script is loaded
  if (!(window as any).Paddle) {
    if (process.env.REACT_APP_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('Paddle.js failed to load');
    }
    return;
  }

  try {
    // Set environment based on NODE_ENV
    const isProduction = process.env.REACT_APP_ENV === 'production';
    if (isProduction) {
      (window as any).Paddle.Environment.set('production');
    } else {
      (window as any).Paddle.Environment.set('sandbox');
    }

    // Initialize Paddle
    const initConfig: any = {
      token,
    };

    // Add customer ID if provided
    if (customerId) {
      // eslint-disable-next-line no-console
      // console.log('customerId', customerId, initConfig);
      initConfig.pwCustomer = { id: customerId || '' };
    }

    (window as any).Paddle.Initialize(initConfig);

    // if (process.env.NODE_ENV === 'development') {
    //   // eslint-disable-next-line no-console
    //   console.log('Paddle initialized successfully');
    // }
  } catch (err) {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('Paddle initialization error:', err);
    }
    throw err;
  }
};

type HighAdvocacyUser = {
  userId: string;
  email: string;
  name: string;
};

type InitializeHighAdvocacyParams = {
  containerId: string;
  campaignId: string;
  user: HighAdvocacyUser;
  darkMode?: boolean;
  onError?: (error: any) => void;
};

// Track the campaign currently being initialized to prevent N+1 script re-injections
// when the hook re-fires (e.g. Redux state arriving in waves: userId → email → name).
let activeHighAdvocacyCampaignId: string | null = null;

export const initializeHighAdvocacy = ({
  containerId,
  campaignId,
  user,
  darkMode = false,
  onError,
}: InitializeHighAdvocacyParams): void => {
  if (!containerId || !campaignId) return;
  if (!user?.userId || !user?.email) return;
  const containerEl = document.getElementById(containerId);
  if (!containerEl) return;

  // Set config (as per vendor snippet) before loading the widget script
  window.HighAdvocacy = {
    containerId,
    campaignId,
    darkMode,
    user,
  } as any;

  const existingScript = document.getElementById('highadvocacy-widget');
  if (existingScript && existingScript.parentNode) {
    if (containerEl.childElementCount > 0) {
      // Widget already rendered — nothing to do.
      return;
    }

    // Script exists but container is still empty.
    // If same campaign, the script is still loading — don't remove & re-inject.
    if (activeHighAdvocacyCampaignId === campaignId) {
      return;
    }

    // Different campaign (SPA navigation) — re-inject for the new campaign.
    const parent = existingScript.parentNode;
    if (parent.contains(existingScript)) {
      parent.removeChild(existingScript);
    }
  }

  activeHighAdvocacyCampaignId = campaignId;

  const script = document.createElement('script');
  script.id = 'highadvocacy-widget';
  script.src = process.env.REACT_APP_HIGH_ADVOCACY_WIDGET_URL;
  script.async = true;

  script.onerror = (event) => {
    const err = new Error('Failed to load HighAdvocacy widget script');
    (err as any).event = event;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    onError?.(err);
  };

  const appendTarget = document.head ?? document.body;
  const firstScript = document.getElementsByTagName('script')[0];
  const parent = firstScript?.parentNode ?? appendTarget;

  try {
    if (firstScript && parent.contains(firstScript)) {
      parent.insertBefore(script, firstScript);
    } else {
      appendTarget.appendChild(script);
    }
  } catch (error) {
    appendTarget.appendChild(script);
    // eslint-disable-next-line no-console
    console.error('Failed to inject HighAdvocacy script:', error);
  }
};

export const initializeBetterBugs = (apiKey: string, email?: string) => {
  if (!apiKey) return;

  if ((window as any).betterBugsInstance) {
    return;
  }

  const createBetterBugsInstance = () => {
    if ((window as any).Betterbugs) {
      const instance = new (window as any).Betterbugs({
        apiKey,
        // Prefills the widget's email field for the logged-in user (production mode only).
        ...(email ? { email } : {}),
        styles: {
          theme: 'light',
          primaryColor: '#ff7119',
          primaryTextColor: '#FFFFFF',
        },
        position: {
          bottom: '50px',
          right: '30px',
        },
        mode: 'production',
        showActionButton: false,
        enableAnnotation: true,
        captureRewindOnScreenshot: true,
        recordType: 'recordVideo',
        maxRecordingMinutes: 5,
        mainHeading: 'Submit Feedback',
        startRecordingBtnText: 'Submit a Feedback',
        mainHeaderText: 'Ready to share your feedback?',
        subHeaderText: 'Record your screen or speak out your thoughts',
        emailFieldLabel: 'Your email',
        titleFieldLabel: 'Feedback title',
        descriptionFieldLabel: 'Describe your feedback',
        emailFieldPlaceholder: 'you@company.com',
        titleFieldPlaceholder: 'What is this feedback about?',
        descriptionFieldPlaceholder:
          "Tell us what worked, what didn't, or what you'd love to see next",
        successMessageHeaderText: 'Thanks for the feedback! 🎉',
        successMessageSubHeaderText:
          "We've saved it and our team will review shortly.",
      });

      (window as any).betterBugsInstance = instance;
    }
  };

  const scriptsAlreadyLoaded = (window as any).Betterbugs;

  if (scriptsAlreadyLoaded) {
    createBetterBugsInstance();
  } else {
    const scriptSrc = 'https://cdn.betterbugs.io/scripts/latest/index.js';

    new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${scriptSrc}"]`,
      );
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error(`Failed to load script: ${scriptSrc}`));
      document.head.appendChild(script);
    })
      .then(() => {
        createBetterBugsInstance();
      })
      .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error loading BetterBugs script:', error);
        }
      });
  }
};
